/**
 * Light / dark theme of the console, chosen by the player on the host platform.
 *
 * At boot: the `theme` attribute of #revenge-bet-console (or `theme` in the page URL)
 * wins over the skin's default theme (uiPersonalization). While the console is open the
 * host sends {type: 'SET_THEME', theme: 'light' | 'dark'}: from the embedding page
 * (iframe) or from the page itself (console embedded as a script, window.postMessage).
 * Nothing reloads: the player stays on the open event, slip, live section.
 *
 * The store (settings/SET_THEME) is the only source: every commit, from the host or from
 * the console's own theme switch, is applied to Vuetify and to the `light` class of <html>.
 */
export const THEME_MESSAGE = 'SET_THEME';

/**
 * @param {*} value
 * @returns {'light'|'dark'|null}
 */
export function themeOrNull(value) {
    return value === 'light' || value === 'dark' ? value : null;
}

function applyToPage(theme, root) {
    document.documentElement.classList.toggle('light', theme === 'light');
    if (root && root.$vuetify) root.$vuetify.theme.dark = theme !== 'light';
}

let installed = false;

/**
 * Applies the store theme now and on every change, and listens to SET_THEME. Once per page.
 *
 * @param {{store: object, root: object}} deps - root is the app's root Vue instance
 */
export function installThemeSync({ store, root }) {
    if (installed) return;
    installed = true;

    applyToPage(store.getters['settings/getTheme'], root);
    store.subscribe((mutation, state) => {
        if (mutation.type === 'settings/SET_THEME') applyToPage(state.settings.theme, root);
    });

    window.addEventListener('message', (event) => {
        const data = event && event.data;
        if (!data || typeof data !== 'object' || data.type !== THEME_MESSAGE) return;
        if (event.source !== window.parent && event.source !== window) return;
        const theme = themeOrNull(data.theme);
        if (!theme) {
            console.warn(`[Theme] ${THEME_MESSAGE} ignored: theme must be 'light' or 'dark'`);
            return;
        }
        if (store.getters['settings/getTheme'] !== theme) store.commit('settings/SET_THEME', theme);
    });
}
